import React, { useEffect, useState, useMemo } from "react";
import { Row, Col } from "antd";
import {
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import styled from "styled-components";

// Import sub-components
import StatsCards from "./Components/StatsCards";
import SubmissionProgress from "./Components/SubmissionProgress";
import TestConfiguration from "./Components/TestConfiguration";
import ImportantDates from "./Components/ImportantDates";
import EvaluateTab from "./Components/EvaluateTab";
import { StyledTabs } from "Pages/MyBatchesAntd/MyBatchesAntd";
import TestEvaluationHeader from "Pages/TestEvaluation/Components/TestEvaluationHeader";
import { useLocation, useNavigate } from "react-router-dom";
import MyBatchSyllabusTab from "Pages/MyBatches/MyBatch/MyBatchTabs/MyBatchSyllabusTab";
import {
  useSubjectiveTest,
  useSubjectiveTestSummary,
} from "Pages/DRS/Context/MyBatchContext";

export const MainContainer = styled.div<{ screen: string }>`
  padding: ${(props) =>
    ["xs", "sm", "md"].includes(props.screen) ? "0px" : "24px 24px 0px 43px"};
  background-color: #f8fbff;
  min-height: 100vh;
`;

interface TestInfo {
  id?: number;
  format?: string;
  mode?: string;
  startDate?: string;
  endDate?: string;
  evaluationDate?: string;
  resultDate?: string;
  testName?: string;
}

const TestEvaluation = () => {
  const [testInfo, setTestInfo] = useState<any>({});
  const { state } = useLocation();
  const {
    batchId,
    batchDetail,
    test,
    action,
    batchDetailsData,
    batchDetailById,
  } =
    (state as {
      batchId: string;
      batchDetail: any;
      test: any;
      batchDetailsData: any;
      action?: string;
      batchDetailById?: any;
    }) || {};
  const { totalNumberOfStudents: TotalStudents = 0 } = batchDetail || {};
  const navigate = useNavigate();
  const { screen } = useWindowSize();
  const [activeTab, setActiveTab] = useState("3");
  const { fetchSubjectiveTest, subjectiveTestData } = useSubjectiveTest();
  const { fetchSubjectiveTestSummary, subjectiveTestSummaryData = {} } =
    useSubjectiveTestSummary();
  const Test_Configuration = (subjectiveTestData as any)?.data || {};
  const [selectedCourseSyllabus, setSelectedCourseSyllabus] = useState<any>(
    batchDetailById?.[0]
  );
  useEffect(() => {
    const { test, tabIndex } =
      (state as { test: TestInfo; tabIndex?: string }) || {};
    if (tabIndex) {
      setActiveTab(tabIndex);
    }
    if (!test?.id) return;
    setTestInfo(test);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    document.body.classList.add("noOverflow");
    return () => {
      document.body.classList.remove("noOverflow");
    };
  }, [state]);
  useEffect(() => {
    if (batchId && testInfo?.id) {
      fetchSubjectiveTest(batchId, testInfo.id, testInfo.testId);
      fetchSubjectiveTestSummary(batchId, testInfo.id);
    }
  }, [batchId, testInfo?.id]);

  const summary = (subjectiveTestSummaryData as any)?.data || {};

  const stats = useMemo(
    () => [
      {
        label: "Submissions",
        value: summary?.submissions?.toString() || "0",
        total: TotalStudents?.toString() || "0",
        icon: <FileTextOutlined />,
        color: "#e6f7ff", // Light blue bg
        iconColor: "#1890ff",
      },
      {
        label: "Evaluated",
        value: summary?.evaluated?.toString() || "0",
        total: (summary?.submissions || 0).toString(),
        icon: <CheckCircleOutlined />,
        color: "#f6ffed", // Light green bg
        iconColor: "#52c41a",
      },
      {
        label: "Pending",
        value: summary?.pending?.toString() || "0",
        total: (summary?.submissions || 0).toString(),
        icon: <ClockCircleOutlined />,
        color: "#fff7e6", // Light orange bg
        iconColor: "#faad14",
      },
      {
        label: "Challenged",
        value: summary?.challenged?.toString() || "0",
        total: (summary?.submissions || 0).toString(),
        icon: <WarningOutlined />,
        color: "#fff1f0", // Light red bg
        iconColor: "#ff4d4f",
      },
    ],
    [
      summary?.submissions,
      summary?.evaluated,
      summary?.pending,
      summary?.challenged,
      TotalStudents,
    ]
  );

  const progressData = useMemo(
    () => ({
      submissionRate:
        summary.submissions && TotalStudents
          ? parseFloat(((summary.submissions / TotalStudents) * 100).toFixed(1))
          : 0,

      evaluationProgress:
        summary.evaluated && summary.submissions
          ? parseFloat(
              ((summary.evaluated / summary.submissions) * 100).toFixed(1)
            )
          : 0,
      submittedCount: summary.submissions || 0,
      totalStudents: TotalStudents,

      gradedCount: summary.evaluated || 0,
    }),
    [summary.submissions, summary.evaluated, TotalStudents]
  );
  const renderOverview = useMemo(
    () => (
      <div style={{ marginTop: 12 }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={14}>
            <TestConfiguration
              subject={Test_Configuration?.subject}
              duration={Test_Configuration?.duration}
              format={testInfo?.format}
              mode={testInfo?.mode}
              totalMarks={Test_Configuration?.totalMarks}
              testId={testInfo?.testId}
              externalTestScheduleId={testInfo?.externalScheduleId}
            />
          </Col>
          <Col xs={24} lg={10}>
            <SubmissionProgress
              submissionRate={progressData.submissionRate}
              evaluationProgress={progressData.evaluationProgress}
              submittedCount={progressData.submittedCount}
              totalStudents={progressData.totalStudents}
              gradedCount={progressData.gradedCount}
            />
            <ImportantDates
              startDate={testInfo.startDate}
              endDate={testInfo.endDate}
              evaluationDate={testInfo.evaluationDate}
              resultDate={test?.testResultDate}
            />
          </Col>
        </Row>
      </div>
    ),
    [
      Test_Configuration?.subject,
      Test_Configuration?.duration,
      Test_Configuration?.totalMarks,
      testInfo?.format,
      testInfo?.mode,
      testInfo?.startDate,
      testInfo?.endDate,
      testInfo?.evaluationDate,
      testInfo?.resultDate,
      progressData,
    ]
  );

  const statsComponent = useMemo(
    () => (
      <StatsCards
        stats={stats.map((s) => ({
          ...s,
          bgColor: s.color, // Using the light color defined in stats array
          color: s.iconColor, // Using the primary color for icons
        }))}
        screen={screen}
      />
    ),
    [stats, screen]
  );
  console.log("courseId and subj in TestEvaluation:", batchDetailById);
  const items = useMemo(
    () => [
      {
        key: "1",
        label: <span className="d-flex gap-2">Overview</span>,
        children: (
          <>
            {statsComponent}
            {renderOverview}
          </>
        ),
      },
      {
        key: "2",
        label: <span className="d-flex gap-2">Syllabus</span>,
        children: (
          <>
            {statsComponent}
            <div style={{ marginTop: 12, textAlign: "center" }}>
              <MyBatchSyllabusTab
                screen={screen}
                selectedCourseSyllabus={selectedCourseSyllabus}
                setSelectedCourseSyllabus={setSelectedCourseSyllabus}
                argBatchId={batchId}
                item={batchDetailById}
              ></MyBatchSyllabusTab>
            </div>
          </>
        ),
      },
      {
        key: "3",
        label: <span className="d-flex gap-2">Evaluate</span>,
        children: (
          <>
            {statsComponent}
            <EvaluateTab
              totalMarks={Test_Configuration?.totalMarks}
              action={action}
              type="evaluate"
            />
          </>
        ),
      },
      {
        key: "4",
        label: <span className="d-flex gap-2">Results</span>,
        children: (
          <>
            {statsComponent}
            <EvaluateTab
              totalMarks={Test_Configuration?.totalMarks}
              action={action}
              type="results"
            />
          </>
        ),
      },
    ],
    [
      statsComponent,
      renderOverview,
      screen,
      selectedCourseSyllabus,
      batchDetailById,
      batchId,
      Test_Configuration?.totalMarks,
      action,
    ]
  );
  const onBackClick = () => {
    const { batchId } = state || {};
    navigate("/admin/mybatch/" + batchId, {
      replace: false,
      state,
    });
  };
  return (
    <MainContainer screen={screen}>
      <TestEvaluationHeader
        activeTab={activeTab}
        testInfo={testInfo}
        batchDetail={batchDetail}
        batchDetailsData={batchDetailsData}
        onBackClick={onBackClick}
        batchDetailById={batchDetailById}
        selectedCourseSyllabus={selectedCourseSyllabus}
        selectedCourseSyllabusHandler={setSelectedCourseSyllabus}
      ></TestEvaluationHeader>
      <StyledTabs activeKey={activeTab} onChange={setActiveTab} items={items} />
    </MainContainer>
  );
};

export default TestEvaluation;
